/**
 * Evidence styles (#/snapshots) — layout only.
 * Visuals inherit the command-center glass system (glass-panel + --cc-* tokens
 * from 22-overview-command-center.css). The page keeps OUR background; only the
 * hub grid, tabs, gallery, timeline, and analytics widgets live here.
 */

export const CITY_OBS_STYLE_ID = "croc-city-observation-v1";

const CITY_OBS_STYLE_TEXT = `
body[data-overview-cc="1"] .croc-cc-page-content .cobs-page {
  width: 100%;
  max-width: none;
  color: var(--cc-text);
}
.cobs-page { display: flex; flex-direction: column; gap: var(--cc-gap, 16px); width: 100%; color: var(--cc-text); }

/* ---- header + tabs ---- */
.cobs-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 14px 18px;
}
.cobs-head__title { display: flex; align-items: center; gap: 10px; }
.cobs-head__ico {
  width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center;
  font-size: 16px; color: #fff; background: linear-gradient(135deg, #0ea5e9, #6366f1);
}
.cobs-head h2 { margin: 0; font-size: 1.05rem; font-weight: 700; letter-spacing: 0.02em; }
.cobs-head__sub { margin: 2px 0 0; font-size: 11px; color: var(--cc-text-muted, #64748b); }
.cobs-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
.cobs-tab {
  padding: 9px 16px; border-radius: 999px; cursor: pointer; font-size: 13px; font-weight: 600;
  border: 1px solid rgba(148, 163, 184, 0.26); background: rgba(148, 163, 184, 0.10); color: var(--cc-text);
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.cobs-tab:hover { background: rgba(148, 163, 184, 0.18); }
.cobs-tab--on {
  background: linear-gradient(135deg, #2563eb, #4f46e5); color: #fff; border-color: transparent;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.28);
}

/* ---- generic panel ---- */
.cobs-panel { padding: 16px 18px; display: flex; flex-direction: column; min-height: 0; }
.cobs-panel__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.cobs-eyebrow { margin: 0; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--cc-text-muted, #64748b); }
.cobs-title { margin: 0; font-size: 0.98rem; font-weight: 700; color: var(--cc-text); }
.cobs-link { font-size: 11px; font-weight: 600; color: var(--cc-accent, #3b82f6); text-decoration: none; }
.cobs-link:hover { text-decoration: underline; }
.cobs-muted { color: var(--cc-text-muted, #64748b); }
.cobs-empty {
  color: var(--cc-text-muted, #64748b); text-align: center; padding: 36px 16px;
  border: 1px dashed rgba(148, 163, 184, 0.3); border-radius: 14px; font-size: 13px;
}

/* ---- snapshot tab grid ---- */
.cobs-snap {
  display: grid;
  grid-template-columns: 244px minmax(0, 1fr) 322px;
  grid-template-rows: auto auto;
  grid-template-areas:
    "side gallery right"
    "side timeline right";
  gap: var(--cc-gap, 16px);
  align-items: start;
}
.cobs-side { grid-area: side; }
.cobs-gallery { grid-area: gallery; }
.cobs-timeline { grid-area: timeline; }
.cobs-right { grid-area: right; display: flex; flex-direction: column; gap: var(--cc-gap, 16px); }
.cobs-bottom { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--cc-gap, 16px); }

/* ---- left filters ---- */
.cobs-side { gap: 14px; }
.cobs-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 12px; }
.cobs-field > span { font-size: 0.62rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: var(--cc-text-muted, #64748b); }
.cobs-field input, .cobs-field select {
  width: 100%; box-sizing: border-box; padding: 9px 11px; border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.28); background: rgba(255, 255, 255, 0.42);
  color: var(--cc-text); font-size: 13px; outline: none;
}
.cobs-field input:focus, .cobs-field select:focus { border-color: rgba(59, 130, 246, 0.55); }
.cobs-toggle { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 12.5px; font-weight: 600; margin-bottom: 12px; }
.cobs-switch { position: relative; width: 40px; height: 22px; border-radius: 999px; background: rgba(148,163,184,0.4); cursor: pointer; transition: background 0.15s; flex: 0 0 auto; }
.cobs-switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: left 0.15s; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
.cobs-switch--on { background: linear-gradient(135deg, #2563eb, #4f46e5); }
.cobs-switch--on::after { left: 20px; }
.cobs-apply { width: 100%; justify-content: center; }
.cobs-quick { display: flex; flex-wrap: wrap; gap: 6px; }
.cobs-quick__pill { padding: 6px 12px; border-radius: 999px; font-size: 11.5px; font-weight: 600; cursor: pointer; border: 1px solid rgba(148,163,184,0.26); background: rgba(148,163,184,0.10); color: var(--cc-text); }
.cobs-quick__pill--on { background: linear-gradient(135deg, #0ea5e9, #6366f1); color: #fff; border-color: transparent; }

/* ---- gallery ---- */
.cobs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 12px; }
.cobs-card {
  border-radius: 14px; overflow: hidden; position: relative;
  background: rgba(255, 255, 255, 0.42); border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}
.cobs-card__media { position: relative; aspect-ratio: 16 / 10; background: #0b1220; overflow: hidden; }
.cobs-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cobs-card__badge {
  position: absolute; top: 8px; left: 8px; z-index: 2;
  padding: 3px 9px; border-radius: 999px; font-size: 10px; font-weight: 800; letter-spacing: 0.04em;
  color: #fff; background: rgba(37, 99, 235, 0.9); box-shadow: 0 2px 8px rgba(0,0,0,0.25);
}
.cobs-card__pin { position: absolute; top: 8px; right: 8px; z-index: 2; font-size: 13px; opacity: 0.9; }
.cobs-card__time {
  position: absolute; bottom: 8px; right: 8px; z-index: 2;
  font-size: 10px; font-weight: 600; color: #fff; padding: 2px 7px; border-radius: 6px;
  background: rgba(15, 23, 42, 0.55); backdrop-filter: blur(2px);
}
.cobs-card__body { padding: 9px 11px; display: flex; flex-direction: column; gap: 5px; }
.cobs-card__dev { font-size: 12.5px; font-weight: 700; color: var(--cc-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cobs-card__meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 11px; color: var(--cc-text-muted, #64748b); }
.cobs-card__actions { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 2px; }

/* ---- timeline ---- */
.cobs-tl { display: flex; gap: 6px; overflow-x: auto; padding: 6px 2px 10px; }
.cobs-tl__node { flex: 0 0 auto; min-width: 116px; display: flex; flex-direction: column; gap: 6px; align-items: center; text-align: center; }
.cobs-tl__dot { width: 12px; height: 12px; border-radius: 50%; background: linear-gradient(135deg, #38bdf8, #2563eb); box-shadow: 0 0 0 4px rgba(59,130,246,0.14); }
.cobs-tl__bar { width: 100%; height: 2px; background: rgba(148,163,184,0.3); position: relative; }
.cobs-tl__ts { font-size: 10.5px; font-weight: 700; color: var(--cc-text); font-variant-numeric: tabular-nums; }
.cobs-tl__lbl { font-size: 10px; color: var(--cc-text-muted, #64748b); max-width: 116px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ---- right column (analysis / donut / similar) ---- */
.cobs-hero-img { width: 100%; aspect-ratio: 16 / 9; border-radius: 12px; object-fit: cover; background: #0b1220; display: block; }
.cobs-stat-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-top: 10px; }
.cobs-stat { border-radius: 11px; padding: 10px; background: rgba(148, 163, 184, 0.10); }
.cobs-stat__k { font-size: 9.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--cc-text-muted, #64748b); }
.cobs-stat__v { font-size: 1.15rem; font-weight: 800; line-height: 1.1; margin-top: 3px; }
.cobs-donut-wrap { display: flex; align-items: center; gap: 16px; }
.cobs-donut { width: 110px; height: 110px; border-radius: 50%; flex: 0 0 auto; position: relative; background: conic-gradient(rgba(148,163,184,0.25) 0 100%); }
.cobs-donut::after { content: ""; position: absolute; inset: 21px; border-radius: 50%; background: var(--cc-card-bg, rgba(255,255,255,0.8)); box-shadow: inset 0 1px 4px rgba(15,23,42,0.1); }
.cobs-donut__c { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 1; }
.cobs-donut__t { font-size: 1.1rem; font-weight: 800; line-height: 1; }
.cobs-donut__l { font-size: 9px; color: var(--cc-text-muted, #64748b); letter-spacing: 0.06em; text-transform: uppercase; }
.cobs-legend { display: flex; flex-direction: column; gap: 7px; flex: 1 1 auto; min-width: 0; }
.cobs-legend__row { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.cobs-legend__sw { width: 10px; height: 10px; border-radius: 3px; flex: 0 0 auto; }
.cobs-legend__n { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cobs-legend__v { font-weight: 700; color: var(--cc-text-muted, #64748b); }
.cobs-similar { display: flex; flex-direction: column; gap: 8px; }
.cobs-similar__row { display: flex; align-items: center; gap: 10px; }
.cobs-similar__thumb { width: 54px; height: 38px; border-radius: 8px; object-fit: cover; background: #0b1220; flex: 0 0 auto; }
.cobs-similar__meta { min-width: 0; }
.cobs-similar__dev { font-size: 12px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cobs-similar__ts { font-size: 10.5px; color: var(--cc-text-muted, #64748b); }

/* ---- bottom mini summaries ---- */
.cobs-kpis { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 8px; }
.cobs-kpi { border-radius: 11px; padding: 11px; background: rgba(148,163,184,0.10); }
.cobs-kpi__v { font-size: 1.2rem; font-weight: 800; line-height: 1; }
.cobs-kpi__k { font-size: 10px; color: var(--cc-text-muted,#64748b); margin-top: 4px; }

@media (max-width: 1200px) {
  .cobs-snap {
    grid-template-columns: 1fr;
    grid-template-areas: "side" "gallery" "timeline" "right";
  }
  .cobs-bottom { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 720px) {
  .cobs-bottom { grid-template-columns: 1fr; }
}
`;

/** @returns {boolean} */
export function ensureCityObservationStyles() {
  if (typeof document === "undefined") return false;
  if (document.getElementById(CITY_OBS_STYLE_ID)) return true;
  const el = document.createElement("style");
  el.id = CITY_OBS_STYLE_ID;
  el.textContent = CITY_OBS_STYLE_TEXT;
  document.head.appendChild(el);
  return true;
}

export function removeCityObservationStyles() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(CITY_OBS_STYLE_ID);
  if (el) el.remove();
}
